// Folder and list layouts; both show contracts as blocks sorted by expected end. Markup only.

import { escHtml as esc } from '../ui/toast';
import { expiryOf, statusOf, type Item, type Leg } from './data';

export interface ViewState {
  items: Item[];
  expanded: Set<string>;
  openId: string | null;
}

const TAG: Record<Leg['venue'], string> = { kalshi: 'K', polymarket: 'PM' };
const UNFILED = 'Unfiled';

function day(t: number | null): string {
  return t === null ? 'No date' : new Date(t).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function clock(t: number | null): string {
  return t === null ? '—' : new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function shortDate(t: number | null): string {
  return t === null ? '' : new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Soonest first; items with no date sink to the end. */
export function byExpiry(a: Item, b: Item): number {
  return (expiryOf(a) ?? Infinity) - (expiryOf(b) ?? Infinity) || a.name.localeCompare(b.name);
}

function block(it: Item, s: ViewState, showPath: boolean): string {
  const exp = expiryOf(it);
  const st = statusOf(it);
  const open = s.openId === it.id;
  const legs = it.legs.map((l) => `<div class="wl-leg-line"><span class="wl-leg ${l.venue}">${TAG[l.venue]}</span>
    <span class="wl-leg-t" title="${esc(l.ticker)}">${esc(l.title)}</span></div>`).join('');
  return `<div class="wl-block${open ? ' open' : ''}${st === 'settled' ? ' done' : ''}">
    <div class="wl-block-h" data-act="open" data-id="${it.id}">
      <div class="wl-when"><span class="wl-when-d">${shortDate(exp)}</span><span class="wl-when-t">${clock(exp)}</span></div>
      <div class="wl-body">
        <div class="wl-name">${esc(it.name)}${it.legs.length > 1 ? '<span class="wl-kind">arb</span>' : ''}</div>
        ${showPath && it.path.length ? `<div class="wl-path">${esc(it.path.join(' / '))}</div>` : ''}
        ${legs}
      </div>
      <span class="wl-state ${st}">${st === 'settled' ? 'Settled' : 'Open'}</span>
    </div>${open ? detail(it) : ''}</div>`;
}

function detail(it: Item): string {
  return `<div class="wl-detail">
    <label class="wl-f">Name<input class="set-in" data-edit="name" data-id="${it.id}" value="${esc(it.name)}"/></label>
    <label class="wl-f">Folder<input class="set-in" data-edit="path" data-id="${it.id}" value="${esc(it.path.join(' / '))}" list="wlFolders" placeholder="${UNFILED}"/></label>
    ${it.legs.map((l) => `<div class="wl-tk"><span class="wl-leg ${l.venue}">${TAG[l.venue]}</span><span data-copy="${esc(l.ticker)}">${esc(l.ticker)}</span></div>`).join('')}
    <div class="wl-d-actions"><span class="pnl-toggle-btn wl-del" data-act="del" data-id="${it.id}">delete</span></div>
  </div>`;
}

function empty(): string {
  return '<div class="pnl-empty">Nothing saved here yet. Use add to save a market or an arb pair.</div>';
}

// ---------- folders

interface Node {
  name: string;
  key: string;
  children: Map<string, Node>;
  items: Item[];
  count: number;
  first: number;
}

function buildTree(items: Item[]): Node {
  const root: Node = { name: '', key: '', children: new Map(), items: [], count: 0, first: Infinity };
  for (const it of items) {
    const path = it.path.length ? it.path : [UNFILED];
    const exp = expiryOf(it) ?? Infinity;
    let n = root;
    path.forEach((seg, i) => {
      let c = n.children.get(seg);
      if (!c) {
        c = { name: seg, key: path.slice(0, i + 1).join('/'), children: new Map(), items: [], count: 0, first: Infinity };
        n.children.set(seg, c);
      }
      c.count++;
      c.first = Math.min(c.first, exp);
      n = c;
    });
    n.items.push(it);
  }
  return root;
}

function folderHtml(n: Node, s: ViewState): string {
  const open = s.expanded.has(n.key);
  const kids = [...n.children.values()].sort((a, b) => a.first - b.first || a.name.localeCompare(b.name));
  const next = Number.isFinite(n.first) ? `${shortDate(n.first)} ${clock(n.first)}` : '';
  return `<div class="wl-folder${open ? ' open' : ''}">
    <div class="wl-folder-h" data-act="toggle" data-key="${esc(n.key)}">
      <span class="wl-caret caret${open ? ' open' : ''}"></span>
      <span class="wl-fname">${esc(n.name)}</span>
      <span class="wl-fmeta">${next ? `<span class="wl-fnext" title="Soonest expected end">${next}</span>` : ''}<span class="wl-count">${n.count}</span></span>
    </div>
    ${open ? `<div class="wl-folder-b">${kids.map((c) => folderHtml(c, s)).join('')}${[...n.items].sort(byExpiry).map((it) => block(it, s, false)).join('')}</div>` : ''}
  </div>`;
}

export function renderFolders(s: ViewState): string {
  if (!s.items.length) return empty();
  const top = [...buildTree(s.items).children.values()].sort((a, b) => a.first - b.first || a.name.localeCompare(b.name));
  return top.map((n) => folderHtml(n, s)).join('');
}

// ---------- flat list by day

export function renderList(s: ViewState): string {
  if (!s.items.length) return empty();
  let last = '';
  return [...s.items].sort(byExpiry).map((it) => {
    const d = day(expiryOf(it));
    const head = d !== last ? `<div class="pnl-hdr wl-day">${d}</div>` : '';
    last = d;
    return head + block(it, s, true);
  }).join('');
}
